import type { NavEntry } from '../../design-system/shell/nav';
import {
  OverviewIcon,
  ClientsIcon,
  AvatarIcon,
  MeasurementsIcon,
  CompositionIcon,
  HistoryIcon,
  GoalIcon,
  NotesIcon,
  SettingsIcon,
} from '../../design-system/shell/icons';
import { hasFeature, noun, type Feature, type ProfileNoun } from '../../lib/features';

export type SectionKey =
  | 'overview'
  | 'avatar'
  | 'measurements'
  | 'composition'
  | 'history'
  | 'goal'
  | 'notes'
  | 'settings';

/**
 * A dashboard nav entry, with what it needs to be shown at all.
 *
 * `requires` drops the entry where that feature is off. `phoneUnless` puts a
 * desktop-only entry on the phone tab bar where that feature is off: on
 * fitness hosts Measurements folds into Statistics (composition), and with no
 * composition there is no Statistics to fold into.
 */
export interface DashboardNavEntry extends NavEntry {
  requires?: Feature;
  phoneUnless?: Feature;
}

/**
 * The concept's sidebar, minus Photos.
 *
 * Photos has no surface behind it, and a nav item that navigates nowhere is
 * worse than an absent one — it reads as a broken feature rather than an
 * unbuilt one. Restore it with the section.
 *
 * `desktopOnly` entries are the ones the phone tab bar drops, leaving the
 * concept's four: Home, Avatar, Statistics, Target. Their content is not lost
 * — Measurements and History fold into Statistics on a phone, and Notes rides
 * on Home, which is what section B of the concept specifies.
 */
export const DASHBOARD_NAV: DashboardNavEntry[] = [
  { key: 'overview', label: 'Overview', short: 'Home', Icon: OverviewIcon },
  { key: 'avatar', label: 'Avatar', Icon: AvatarIcon },
  {
    key: 'measurements',
    label: 'Measurements',
    Icon: MeasurementsIcon,
    desktopOnly: true,
    phoneUnless: 'composition',
  },
  {
    key: 'composition',
    label: 'Composition',
    short: 'Statistics',
    Icon: CompositionIcon,
    requires: 'composition',
  },
  // Without the fat/lean chart, History is the Measurements grid a second
  // time, so it goes with composition.
  {
    key: 'history',
    label: 'History & graphs',
    Icon: HistoryIcon,
    desktopOnly: true,
    requires: 'composition',
  },
  { key: 'goal', label: 'Goal & simulation', short: 'Target', Icon: GoalIcon, requires: 'goal' },
  { key: 'notes', label: 'Notes', Icon: NotesIcon, desktopOnly: true },
  {
    key: 'settings',
    label: 'Settings',
    Icon: SettingsIcon,
    footer: true,
    desktopOnly: true,
  },
];

/** Legacy `?show=` values, from when the tabs were Ionic's. */
const LEGACY: Record<string, SectionKey> = {
  home: 'overview',
  avatar: 'avatar',
  trends: 'composition',
  overall: 'goal',
};

export const sectionFromQuery = (show: string | null): SectionKey | null =>
  show ? (LEGACY[show] ?? (show as SectionKey)) : null;

/**
 * The entries this deployment shows: those whose feature is on, with
 * `phoneUnless` resolved into `desktopOnly`.
 */
export const dashboardNav = (
  has: (f: Feature) => boolean = hasFeature,
  entries: DashboardNavEntry[] = DASHBOARD_NAV,
): DashboardNavEntry[] =>
  entries
    .filter((e) => !e.requires || has(e.requires))
    .map((e) =>
      e.phoneUnless && !has(e.phoneUnless) ? { ...e, desktopOnly: false } : e,
    );

/**
 * The section a `?show=` value lands on, given the entries on offer. Unknown
 * keys, and sections this deployment does not have (`?show=goal` on retail),
 * land on the overview rather than on an empty page.
 */
export const landingSection = (
  show: string | null,
  entries: Pick<NavEntry, 'key'>[],
): SectionKey => {
  const asked = sectionFromQuery(show);
  return asked && entries.some((e) => e.key === asked) ? asked : 'overview';
};

/**
 * The coach's own workspace nav, from the reference deck's slides 7 and 9:
 * two main surfaces — Overview and Clients — with the supporting ones beside
 * them. Distinct from DASHBOARD_NAV above, which is the nav *within* one
 * selected client.
 *
 * Scanning and Reports are NOT listed. The deck's information architecture
 * puts them here and they will belong here, but neither has a surface yet,
 * and both were routing to the client list — a menu item that silently sends
 * you somewhere else is worse than an absent one, because the reader assumes
 * they misclicked. Restore each with its screen.
 */
export const coachNav = (n: ProfileNoun = noun): NavEntry[] => [
  { key: 'overview', label: 'Overview', Icon: OverviewIcon },
  { key: 'clients', label: n.Clients, Icon: ClientsIcon },
];

export const COACH_NAV: NavEntry[] = coachNav();
